import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik } from 'formik'
import { Col, Select } from 'antd'
import shallow from 'zustand/shallow'

import BackgroundBox from '../BackgroundBox'
import PerButton from '../Button'
import PerFlexRow from '../FlexRow'
import PerIcon from '../Icon'
import PerText from '../Text'
import PerSpacer from '../Spacer'
import { ForecastModalFilterSelect, useForecastModalStore, ForecastModalFilterValues } from '.'
import { EmployeeLevelEnum, EmployeeTagType, CareerPathEvaluationState } from '../../../types'
import { Colors } from '../../util/types'
import { rankingUiProps } from '../../../modules/cultivatingTalents/ranking.uiprops'
import { usePyramidsStore } from '../../../modules/pyramids/PyramidsStore'

const { Option } = Select

const style = { width: '100%' }

export const ForecastModalEmployeeFiltersForm: FC<{ toggleFilterForm: (showFilters: boolean) => void }> = ({ toggleFilterForm }) => {
  const { t } = useTranslation()
  const { isPerformanceTag } = rankingUiProps()
  const [filterValues, setFilterValues, filterEmployees, filterBu, filterEmployeeLevels] = useForecastModalStore(
    (state) => [state.filterValues, state.setFilterValues, state.filterEmployees, state.filterBu, state.filterEmployeeLevels],
    shallow,
  )
  const [isCareerPathEnabled] = usePyramidsStore((state) => [state.isCareerPathEnabled], shallow)

  const filterBuData = filterBu.map((bu) => (
    <Option key={bu.id} value={bu.name} className={bu.isExpired ? 'bu-expired' : undefined}>
      <span className={bu.isExpired ? 'bu-expired' : undefined}>{bu.name}</span>
    </Option>
  ))

  const filterEmployeeData = filterEmployees.map((employee) => (
    <Option key={employee.id} value={`${employee.firstName} ${employee.lastName}`}>
      {`${employee.firstName} ${employee.lastName}`}
    </Option>
  ))

  const emmployeeLevelData = filterEmployeeLevels
    .filter((level: EmployeeLevelEnum) => level !== EmployeeLevelEnum.Leaver)
    .map((level: EmployeeLevelEnum) => (
      <Option key={level} value={level}>
        {level}
      </Option>
    ))

  const filterCareerPathData = Object.keys(CareerPathEvaluationState).map((tag, index, arr) => {
    return (
      <Option key={tag} value={CareerPathEvaluationState[tag]}>
        <PerFlexRow align="middle">
          <PerIcon name="circle_fill" color={['success', 'warning', 'error'][index % arr.length] as Colors} size={10} />
          <PerSpacer ml="xxxs" mr="xxxs" />
          {
            [
              t('pyramidsView.forecastModal.path.on'),
              t('pyramidsView.forecastModal.path.partially'),
              t('pyramidsView.forecastModal.path.not'),
            ][index % arr.length]
          }
        </PerFlexRow>
      </Option>
    )
  })

  const filterTagsData = Object.keys(EmployeeTagType)
    .filter((tag) => {
      return isPerformanceTag(EmployeeTagType[tag]) || EmployeeTagType[tag] === EmployeeTagType.Leaver
    })
    .map((tag) => {
      const enumValue = String(EmployeeTagType[tag]).replace(/_/g, ' ').toLowerCase()
      const label = enumValue.charAt(0).toUpperCase() + enumValue.slice(1)
      return (
        <Option key={tag} value={EmployeeTagType[tag]}>
          {label}
        </Option>
      )
    })

  const handleOnCancel = () => {
    toggleFilterForm(false)
  }

  const handleOnSubmit = (values: ForecastModalFilterValues) => {
    setFilterValues(values)
  }

  return (
    <BackgroundBox className="fullWidth" color="gray-2" noShadow>
      <Formik initialValues={filterValues} enableReinitialize onSubmit={handleOnSubmit}>
        {({ handleSubmit }) => {
          return (
            <>
              <PerFlexRow mb="l" align="middle" gutter={16}>
                <Col span={8}>
                  <PerFlexRow mb="xxxs">
                    <PerText appearance="label-small" fontWeight="normal">
                      {t('pyramidsView.forecastModal.filters.employeeFullName')}
                    </PerText>
                  </PerFlexRow>
                  <ForecastModalFilterSelect
                    name="employeeFullName"
                    placeholder={t('pyramidsView.forecastModal.filters.employeePlaceholder')}
                    showSearch
                    allowClear
                    style={style}
                    notFoundContent={t('meetingInvite.notFoundEmployee')}
                    filterOption={(input, option) => {
                      return option.props && option.props.children
                        ? option.props.children.toString().toLocaleLowerCase().includes(input.trim().toLowerCase())
                        : false
                    }}
                  >
                    {filterEmployeeData}
                  </ForecastModalFilterSelect>
                </Col>
                <Col span={8}>
                  <PerFlexRow mb="xxxs">
                    <PerText appearance="label-small" fontWeight="normal" transform="uppercase">
                      {t('pyramidsView.forecastModal.filters.businessUnitName')}
                    </PerText>
                  </PerFlexRow>
                  <ForecastModalFilterSelect
                    name="businessUnitName"
                    placeholder={t('pyramidsView.forecastModal.filters.buPlaceholder')}
                    allowClear
                    style={style}
                  >
                    {filterBuData}
                  </ForecastModalFilterSelect>
                </Col>
                <Col span={8}>
                  <PerFlexRow mb="xxxs">
                    <PerText appearance="label-small" fontWeight="normal">
                      {t('pyramidsView.forecastModal.filters.currentEmployeeLevel')}
                    </PerText>
                  </PerFlexRow>
                  <ForecastModalFilterSelect
                    name="currentEmployeeLevel"
                    placeholder={t('pyramidsView.forecastModal.filters.levelPlaceholder')}
                    allowClear
                    style={style}
                  >
                    {emmployeeLevelData}
                  </ForecastModalFilterSelect>
                </Col>
              </PerFlexRow>
              <PerFlexRow mb="l" align="middle" gutter={16}>
                {isCareerPathEnabled && (
                  <Col span={8}>
                    <PerFlexRow mb="xxxs">
                      <PerText appearance="label-small" fontWeight="normal">
                        {t('pyramidsView.forecastModal.filters.careerPath')}
                      </PerText>
                    </PerFlexRow>
                    <ForecastModalFilterSelect
                      name="careerPath"
                      placeholder={t('pyramidsView.forecastModal.filters.pathPlaceholder')}
                      allowClear
                      style={style}
                    >
                      {filterCareerPathData}
                    </ForecastModalFilterSelect>
                  </Col>
                )}
                <Col span={8}>
                  <PerFlexRow mb="xxxs">
                    <PerText appearance="label-small" fontWeight="normal">
                      {t('pyramidsView.forecastModal.filters.tags')}
                    </PerText>
                  </PerFlexRow>
                  <ForecastModalFilterSelect
                    name="tags"
                    placeholder={t('pyramidsView.forecastModal.filters.tagsPlaceholder')}
                    allowClear
                    style={style}
                  >
                    {filterTagsData}
                  </ForecastModalFilterSelect>
                </Col>
                <Col span={8}></Col>
              </PerFlexRow>
              <PerFlexRow align="middle" justify="end" gutter={16}>
                <PerButton
                  text={t('global.button.cancel')}
                  color="secondary"
                  before="close_stroke"
                  type="button"
                  onClick={handleOnCancel}
                />
                <PerSpacer ml="xxxs" mr="xxxs" />
                <PerButton
                  text={t('global.button.apply')}
                  color="success"
                  before="check_stroke"
                  type="button"
                  onClick={() => handleSubmit()}
                />
              </PerFlexRow>
            </>
          )
        }}
      </Formik>
    </BackgroundBox>
  )
}
